<template>
 <AccessDenied
  v-if="!permissaoTela.visualizar"
  msg="Você não tem permissão para visualizar esta tela. Caso precise de acesso, procure o suporte."
 />
 <div v-else class="col-md-12 mr-auto ml-auto">
  <div class="wizard-container">
   <div class="card card-wizard active" data-color="primary">
    <Form @submit="onSubmit" :validation-schema="schema">
     <div class="card-header historico-header">
      <div class="historico-actions">
       <router-link :to="{ name: 'patbem' }" class="btn btn-warning btn-sm">
        <i class="uil uil-search" aria-hidden="true"></i>Pesquisar
       </router-link>
       <button v-if="permissaoTela.incluir" type="button" class="btn btn-secondary btn-sm" @click="novo">
        <i class="uil uil-plus" aria-hidden="true"></i>Novo
       </button>
       <button v-if="podeGravar" type="submit" class="btn btn-primary btn-sm" :disabled="loadingSave">
        <i class="ri-save-line" aria-hidden="true"></i>Salvar
       </button>
       <button v-if="permissaoTela.excluir" type="button" class="btn btn-danger btn-sm" :disabled="!model.pesHistoricoId || loadingSave" @click="excluir">
        <i class="uil uil-trash-alt" aria-hidden="true"></i>Excluir
       </button>
      </div>
     </div>

     <div class="card-body">
      <div class="row g-3">
       <div class="col-xl-5 col-lg-6">
        <div class="historico-region">
         <ConsulteGrid :columns="gridColumns" :data-source="dataSource" :actions="true" titulo-actions="Selecionar" hide-header>
          <template #actions="context">
           <button type="button" class="btn btn-outline-primary btn-sm" @click="selecionar(context.row)">
            Selecionar
           </button>
          </template>
         </ConsulteGrid>
        </div>
       </div>

       <div class="col-xl-7 col-lg-6">
        <div class="historico-region historico-form">
         <div class="row">
          <div class="col-md-4 mb-3">
           <div class="form-group">
            <label>Data</label>
            <Field name="data" class="form-control" type="date" v-model="model.data" />
            <ErrorMessage name="data" />
           </div>
          </div>

          <div class="col-md-8 mb-3">
           <div class="form-group">
            <Field
             name="tipoMovimentacao"
             :modelValue="model.tipoMovimentacao"
             @update:modelValue="model.tipoMovimentacao = $event"
             v-slot="{ handleChange }"
            >
             <DropDownListTipoMovimentacao
              :modelValue="model.tipoMovimentacao"
              @update:modelValue="
               (value) => {
                model.tipoMovimentacao = value;
                handleChange(value);
               }
              "
             />
            </Field>
            <ErrorMessage name="tipoMovimentacao" />
           </div>
          </div>

          <div v-if="mostrarRetorno" class="col-md-4 mb-3">
           <div class="form-group">
            <label>Data de Retorno</label>
            <Field name="dataRetorno" class="form-control" type="date" v-model="model.dataRetorno" />
            <ErrorMessage name="dataRetorno" />
           </div>
          </div>

          <template v-if="mostrarDestino">
           <div class="col-md-8 mb-3">
            <div class="form-group">
             <DropDownListEscola
              :key="`escola-${formKey}-${model.gesEscolaId ?? 'novo'}`"
              v-model="model.gesEscolaId"
              label="Escola Destino"
             />
             <ErrorMessage name="gesEscolaId" />
            </div>
           </div>

           <div class="col-md-8 mb-3">
            <div class="form-group">
             <DropDownListSala
              :key="`sala-${formKey}-${model.gesEscolaId ?? 'nova'}`"
              v-model="model.gesSalaId"
              :gesEscolaId="model.gesEscolaId"
              label="Sala/Ambiente"
             />
             <ErrorMessage name="gesSalaId" />
            </div>
           </div>

           <div class="col-md-8 mb-3">
            <div class="form-group">
             <label>Localização</label>
             <Field name="localizacao" class="form-control" type="text" v-model="model.localizacao" />
             <ErrorMessage name="localizacao" />
            </div>
           </div>
          </template>

          <div v-if="mostrarFornecedor" class="col-md-8 mb-3">
           <div class="form-group">
            <DropDownListFornecedor v-model="model.gesFornecedorManutencaoId" />
            <ErrorMessage name="gesFornecedorManutencaoId" />
           </div>
          </div>

          <div class="col-12 mb-3">
           <div class="form-group">
            <label>Observação</label>
            <Field
             name="observacao"
             as="textarea"
             class="form-control"
             rows="4"
             v-model="model.observacao"
            />
            <ErrorMessage name="observacao" />
           </div>
          </div>
         </div>
        </div>
       </div>
      </div>
     </div>
    </Form>
   </div>
  </div>
 </div>
</template>

<script lang="ts">
import ConsulteGrid from "@/components/grid/ConsulteGrid.vue";
import { ConsulteGridColumnsModel } from "@/datasources/consulte-grid";
import { Form, Field, ErrorMessage } from "vee-validate";
import { defineComponent } from "vue";
import * as yup from "yup";
import PatHistoricoBemDataSource from "@/datasources/patrimonio/pathistoricobem/index.datasource";
import { PatHistoricoBemModel } from "@/models/patrimonio/pathistoricobem/pathistoricobem.model";
import PatHistoricoBemService from "@/services/patrimonio/pathistoricobem/pathistoricobem.service";
import toastService from "@/services/globals/toast.service";
import DropDownListFornecedor from "@/components/formulario/gestao/DropDownListFornecedor.vue";
import DropDownListEscola from "@/components/formulario/gestao/DropDownListEscola.vue";
import DropDownListSala from "@/components/formulario/gestao/DropDownListSala.vue";
import DropDownListTipoMovimentacao from "@/components/formulario/patrimonio/DropDownListTipoMovimentacao.vue";
import { PermissaoTela, resolverPermissaoTela } from "@/composables/usePermissaoTela/usePermissaoTela";
import { eTipoPermissao } from "@/enums/ePermissoes";
import AccessDenied from "@/views/auth/AccessDenied.vue";

export default defineComponent({
 name: "PatHistoricoBemCadastroComGrid",
 components: {
  ConsulteGrid,
  Form,
  Field,
  ErrorMessage,
  DropDownListFornecedor,
  DropDownListEscola,
  DropDownListSala,
  DropDownListTipoMovimentacao,
  AccessDenied,
 },
 computed: {
  permissaoTela(): PermissaoTela {
   return resolverPermissaoTela(this.$route.meta?.permissions as eTipoPermissao[] | undefined);
  },
  // Movimentação nova exige incluir; alterar uma já gravada exige alterar.
  podeGravar(): boolean {
   return this.model.pesHistoricoId ? this.permissaoTela.alterar : this.permissaoTela.incluir;
  },
  mostrarRetorno(): boolean {
   const tipo = Number(this.model.tipoMovimentacao);
   return [0, 1, 3].includes(tipo);
  },
  mostrarDestino(): boolean {
   const tipo = Number(this.model.tipoMovimentacao);
   return [1, 2, 5].includes(tipo);
  },
  mostrarFornecedor(): boolean {
   return Number(this.model.tipoMovimentacao) === 0;
  },
 },
 methods: {
  criarModel(): PatHistoricoBemModel {
   const model = new PatHistoricoBemModel();
   model.patBemId = this.patBemId;
   model.tipoMovimentacao = 0;
   return model as PatHistoricoBemModel;
  },
  novo() {
   this.formKey++;
   this.model = this.criarModel();
  },
  selecionar(row: PatHistoricoBemModel) {
   const model = { ...row } as PatHistoricoBemModel;
   (model as any).data = this.formatDateForInput(row.data);
   (model as any).dataRetorno = this.formatDateForInput(row.dataRetorno);
   model.patBemId = this.patBemId;
   this.model = model;
  },
  async carregarGrid() {
   this.dataSource.setPatBemId(this.patBemId);
   this.dataSource.loading = true;
   try {
    await this.dataSource.load();
   } finally {
    this.dataSource.loading = false;
   }
  },
  formatDateForInput(value: Date | string | undefined): string | undefined {
   if (!value)
    return undefined;

   if (typeof value === "string")
    return value.substring(0, 10);

   return value.toISOString().substring(0, 10);
  },
  formatDateGrid(index: number, dataField: string, value: Date | string | undefined): string {
   if (!value)
    return "";

   const dateText = typeof value === "string" ? value.substring(0, 10) : value.toISOString().substring(0, 10);
   const [year, month, day] = dateText.split("-");
   if (!year || !month || !day)
    return "";

   return `${day}/${month}/${year}`;
  },
  limparCamposOcultos() {
   if (!this.mostrarRetorno)
    this.model.dataRetorno = undefined;

   if (!this.mostrarDestino) {
    this.model.gesEscolaId = 0;
    this.model.gesSalaId = 0;
    this.model.localizacao = "";
   }

   if (!this.mostrarFornecedor)
    this.model.gesFornecedorManutencaoId = 0;
  },
  async onSubmit() {
   if (!this.podeGravar)
    return;

   if (!this.patBemId) {
    toastService.alertaErro("Bem nao informado.");
    return;
   }

   this.model.patBemId = this.patBemId;
   this.model.pesHistoricoId = this.model.pesHistoricoId ?? 0;
   this.limparCamposOcultos();
   this.loadingSave = true;

   try {
    await PatHistoricoBemService.inserirAtualizar(this.model);
    toastService.alertaSucesso("Dados registrados com sucesso!");
    this.novo();
    await this.carregarGrid();
   } catch (error) {
    console.error("Erro ao salvar Historico do Bem:", error);
    toastService.alertaErro("Ocorreu um erro ao executar a acao.");
   } finally {
    this.loadingSave = false;
   }
  },
  async excluir() {
   if (!this.model.pesHistoricoId || !this.permissaoTela.excluir)
    return;

   const result = await toastService.confirmDelete("Histórico do Bem");
   if (!result.isConfirmed)
    return;

   await PatHistoricoBemService.remover(this.model.pesHistoricoId);
   toastService.alertaSucesso("Registro excluido com sucesso!");
   this.novo();
   await this.carregarGrid();
  },
 },
 watch: {
  "$route.params.patBemId"(value: string) {
   this.patBemId = Number(value || 0);
   this.novo();
   this.carregarGrid();
  },
  "model.tipoMovimentacao"() {
   this.limparCamposOcultos();
  },
 },
 mounted() {
  this.carregarGrid();
 },
 data() {
  const patBemId = Number(this.$route.params.patBemId || 0);
  const dataSource = new PatHistoricoBemDataSource(0, 10, 0, 0, []);
  dataSource.setPatBemId(patBemId);

  const model = new PatHistoricoBemModel();
  model.patBemId = patBemId;
  model.tipoMovimentacao = 0;

  return {
   patBemId,
   dataSource,
   loadingSave: false,
   formKey: 0,
   model: model as PatHistoricoBemModel,
   schema: yup.object({
    data: yup.date().nullable().required().label("Data"),
    tipoMovimentacao: yup.number().nullable().required().label("Tipo de Movimentação"),
    dataRetorno: yup.date().nullable().label("Data de Retorno"),
    gesEscolaId: yup.number().nullable().label("Escola Destino"),
    gesSalaId: yup.number().nullable().label("Sala/Ambiente"),
    localizacao: yup.string().nullable().max(100).label("Localização"),
    gesFornecedorManutencaoId: yup.number().nullable().label("Fornecedor"),
    observacao: yup.string().nullable().max(500).label("Observação"),
   }),
   gridColumns: [
    {
     text: "Data",
     align: "center",
     cellsalign: "center",
     datafield: "data",
     editable: false,
     filtertype: "text",
     filterable: false,
     cellsrenderer: this.formatDateGrid,
    },
    {
     text: "Tipo de Movimentação",
     align: "center",
     cellsalign: "left",
     datafield: "descricaoTipoMovimentacao",
     editable: false,
     filtertype: "text",
     filterable: false
    },
   ] as ConsulteGridColumnsModel[],
  };
 },
});
</script>

<style scoped>
.historico-header {
 align-items: center;
 display: flex;
 gap: 1rem;
}

.historico-actions {
 display: flex;
 flex-wrap: wrap;
 gap: 0.5rem;
}

.historico-region {
 border: 1px solid #e1e5ea;
 border-radius: 6px;
 height: 100%;
 padding: 1rem;
}

.historico-form {
 max-width: 44rem;
}
</style>
